import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PortfolioCard from "./PortfolioCard";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { toAbsoluteUrl } from "../utils/url";

const container = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
      when: "beforeChildren",
      staggerChildren: 0.12,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 24, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55 } },
};

function PortfolioHome() {
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const q = query(
          collection(db, "projects"),
          orderBy("createdAt", "desc"),
          limit(4)
        );
        const snap = await getDocs(q);
        if (!mounted) return;
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            // map Firestore fields to PortfolioCard expected keys
            title: data.title || "Untitled",
            image: data.imageUrl || data.image || "",
            link: data.liveLink ? toAbsoluteUrl(data.liveLink) : "#",
            category: (data.tags && data.tags[0]) || "Project",
            tech: Array.isArray(data.tech) ? data.tech : [],
          };
        });
        // Incrementally append items to state to show them as they load
        setLatest([]);
        let first = true;
        for (const row of rows) {
          if (!mounted) break;
          setLatest((prev) => [...prev, row]);
          if (first) {
            first = false;
            // allow first item to appear immediately
            setLoading(false);
          }
          // yield to UI thread so each item can render
          await new Promise((r) => setTimeout(r, 0));
        }
      } catch (e) {
        // fail silently for homepage section; keep empty list
        console.error("Failed to load latest projects:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="portfolio"
      className="py-4 sm:py-8 lg:py-8 bg-white text-gray-800"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.3 }}
          className="flex items-center justify-between mb-8 sm:mb-10"
        >
          <motion.div variants={item} className="space-y-2">
            <p className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: "rgb(232, 51, 16)" }}
              />
              My Portfolio
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight">
              My Latest{" "}
              <span style={{ color: "rgb(232, 51, 16)" }}>Projects</span>
            </h2>
          </motion.div>

          <motion.div variants={item}>
            <Link
              to="/portfolio"
              className=" text-[12px] group flex items-center gap-2 font-semibold bg-[#211033] text-white border border-teal-700 rounded-xl py-2 px-4 max-h-[40px] hover:bg-white hover:text-[#211033] ease-in duration-300 shadow"
            >
              View All Projects
            </Link>
          </motion.div>
        </motion.div>

        {/* Portfolio Grid - two columns, latest 4 only */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.15 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-4 sm:gap-x-5"
        >
          {latest.length === 0 && loading ? (
            // Skeleton placeholders (4 cards)
            [...Array(4)].map((_, i) => (
              <div key={i} className="relative rounded-2xl bg-gray-50 border border-gray-200 shadow-sm overflow-hidden py-2 px-4 max-w-[93%] mx-auto">
                <div className="relative w-full h-[19rem] sm:h-[19rem] bg-gray-200 animate-pulse rounded-t-2xl" />
                <div className="p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1 rounded-full bg-teal-200/60 text-teal-800 animate-pulse">&nbsp;</span>
                    <span className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1 rounded-full bg-teal-100 text-teal-800 animate-pulse">&nbsp;</span>
                  </div>
                  <div className="h-5 w-2/3 bg-gray-200 rounded animate-pulse" />
                  <div className="mt-2 h-3 w-24 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            ))
          ) : latest.length === 0 ? (
            <div className="md:col-span-2 text-sm text-gray-500">No projects found.</div>
          ) : (
            <>
              {latest.map((p) => <PortfolioCard key={p.id} project={p} />)}
              {loading && latest.length < 4 && [...Array(4 - latest.length)].map((_, i) => (
                <div key={`sk-${i}`} className="relative rounded-2xl bg-gray-50 border border-gray-200 shadow-sm overflow-hidden py-2 px-4 max-w-[93%] mx-auto">
                  <div className="relative w-full h-[19rem] sm:h-[19rem] bg-gray-200 animate-pulse rounded-t-2xl" />
                  <div className="p-4">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1 rounded-full bg-teal-200/60 text-teal-800 animate-pulse">&nbsp;</span>
                      <span className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1 rounded-full bg-teal-100 text-teal-800 animate-pulse">&nbsp;</span>
                    </div>
                    <div className="h-5 w-2/3 bg-gray-200 rounded animate-pulse" />
                    <div className="mt-2 h-3 w-24 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}

export default PortfolioHome;
